import cx from "classnames";
import { ForwardedRef, useRef } from "react";
import { mergeRefs } from "@react-aria/utils";

import Draggable from "components/DragAndDrop/Draggable";
import DraggableItem from "ds/components/DraggableItem";

import styles from "./styles.module.css";
import { DragDropListDraggableItemProps } from "./types";

const DragDropListDraggableItem = <
  T extends string,
  U extends Record<string, unknown> | undefined,
>({
  id,
  disabled,
  className,
  noBorder,
  size = "small",
  renderItemContent,
  renderItemProps,
  columnId,
}: DragDropListDraggableItemProps<T, U>) => {
  const handleRef = useRef<HTMLDivElement>(null);

  return (
    <Draggable id={id} disabled={disabled}>
      {({
        isDragging,
        setActivatorNodeRef,
        setNodeRef,
        transform,
        transition,
        listeners,
        attributes,
      }) => {
        const style = transform
          ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
              transition,
              transformOrigin: "center",
            }
          : undefined;

        if (handleRef.current && document.activeElement !== handleRef.current && isDragging) {
          // it solves focus issue when dragging inside drawer
          handleRef.current.focus();
        }

        return (
          <DraggableItem
            ref={setNodeRef}
            size={size}
            dragging={isDragging}
            disabled={disabled}
            className={cx(styles.draggable, isDragging && styles.dragging, className)}
            style={style}
            noBorder={noBorder}
          >
            {renderItemContent({
              id,
              disabled,
              columnId,
              dragging: isDragging,
              handleRef: mergeRefs(setActivatorNodeRef, handleRef) as ForwardedRef<HTMLDivElement>,
              handleListeners: listeners,
              handleAttributes: attributes,
              props: renderItemProps,
            })}
          </DraggableItem>
        );
      }}
    </Draggable>
  );
};

export default DragDropListDraggableItem;
